import { Link } from "react-router-dom";
import { Clock, TrendingUp, PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import AdSlot from "@/components/AdSlot";
import EventsWidget from "@/components/EventsWidget";
import EPaperButton from "@/components/EPaperButton";
import PushSubscribeButton from "@/components/PushSubscribeButton";
import TrendingSidebar from "@/components/TrendingSidebar";
import PanchangWidget from "@/components/PanchangWidget";
import FuelPricesStrip from "@/components/FuelPricesStrip";
import InstallAppButton from "@/components/InstallAppButton";
import MorningBriefingCard from "@/components/MorningBriefingCard";
import EditorBytesStrip from "@/components/EditorBytesStrip";
import HomeGauravStrip from "@/components/HomeGauravStrip";
import TopClappedWidget from "@/components/TopClappedWidget";
import TopReadStrip from "@/components/TopReadStrip";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "अभी";
  if (diff < 3600) return `${Math.floor(diff / 60)} मिनट पहले`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} घंटे पहले`;
  return d.toLocaleDateString("hi-IN");
}

export default function HomePage() {
  const [news, setNews] = useState([]);
  const [editorials, setEditorials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/news", { params: { limit: 30 } }),
      api.get("/news", { params: { editorial: true, limit: 3 } }),
    ]).then(([n, e]) => { setNews(n.data); setEditorials(e.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (<><PublicHeader /><div className="max-w-7xl mx-auto p-10 text-slate-500 text-center">लोड हो रहा है…</div></>);

  const hero = news[0];
  const featured = news.slice(1, 5);
  const trending = news.slice(0, 6);
  const rest = news.slice(5);

  return (
    <>
      <PublicHeader />
      <main className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8" data-testid="home-page">
        {/* Morning Briefing — weather + AQI + fuel + panchang in one card */}
        <MorningBriefingCard />

        {/* E-Paper + Push CTA banner */}
        <section className="mb-6 md:mb-8 rounded-xl bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white p-4 md:p-8 relative overflow-hidden" data-testid="epaper-push-cta">
          <div className="absolute -top-8 -right-6 opacity-5 text-[140px] md:text-[180px] font-devnagri-heading font-black leading-none pointer-events-none select-none">21</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-center relative">
            <div>
              <div className="uppercase tracking-[0.25em] text-[10px] text-amber-300 font-bold mb-1.5 md:mb-2">Today's e-Paper</div>
              <h2 className="font-devnagri-heading text-lg md:text-3xl font-black leading-tight">आज का पूरा अख़बार <span className="text-amber-300">एक क्लिक में</span></h2>
              <p className="text-slate-300 text-xs md:text-sm mt-1.5 md:mt-2 mb-3 md:mb-4 hidden sm:block">प्रधान संपादक <b className="text-amber-300">JAKIR MOMIN</b> की चुनिंदा ख़बरें — PDF डाउनलोड कीजिये या WhatsApp पर परिवार को भेजिये।</p>
              <EPaperButton variant="hero"/>
            </div>
            <div className="border-t md:border-t-0 md:border-l border-white/10 pt-4 md:pt-0 md:pl-8">
              <div className="uppercase tracking-[0.25em] text-[10px] text-red-300 font-bold mb-1.5 md:mb-2">Breaking News Alerts</div>
              <h3 className="font-devnagri-heading text-base md:text-2xl font-black leading-tight">बड़ी ख़बरें, <span className="text-red-300">तुरंत आपके पास</span></h3>
              <p className="text-slate-300 text-xs md:text-sm mt-1.5 md:mt-2 mb-3 md:mb-4 hidden sm:block">एक बार अनुमति दीजिये — फिर हर ब्रेकिंग न्यूज़ की सूचना सीधे आपके फ़ोन/ब्राउज़र पर आएगी।</p>
              <PushSubscribeButton variant="hero"/>
            </div>
          </div>
        </section>

        <AdSlot placement="home_top" className="mb-6 md:mb-8"/>

        <HomeGauravStrip />

        <TopReadStrip />

        <TopClappedWidget />

        <EditorBytesStrip />

        <div className="hidden md:block">
          <FuelPricesStrip />
        </div>

        <PanchangWidget />
        {hero && (
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 mb-8 md:mb-12">
            <Link to={`/article/${hero.slug}`} className="lg:col-span-8 group" data-testid="hero-article">
              <div className="img-hover-zoom relative rounded-lg aspect-[16/10] bg-slate-200 mb-3 md:mb-5">
                {hero.image_url && <img src={hero.image_url} alt={hero.title} className="w-full h-full object-cover" loading="lazy"/>}
                {hero.video_url && (
                  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 bg-[#DC2626] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md shadow-lg" data-testid="hero-video-badge">
                    <PlayCircle className="w-3.5 h-3.5"/> Video
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3 flex-wrap">
                <span className="tag-cat">{hero.category}</span>
                {hero.is_breaking && <span className="tag-breaking">ब्रेकिंग</span>}
                <span className="text-[11px] md:text-xs text-slate-500 font-admin flex items-center gap-1"><Clock className="w-3 h-3"/>{relTime(hero.created_at)}</span>
              </div>
              <h1 className="font-devnagri-heading text-xl sm:text-3xl lg:text-5xl font-black leading-tight text-[#0F172A] news-title">
                {hero.title}
              </h1>
              <p className="mt-2 md:mt-4 text-sm md:text-lg text-slate-600 leading-relaxed line-clamp-3">{hero.excerpt}</p>
              <div className="mt-2 md:mt-3 text-[10px] md:text-xs text-slate-500 font-admin uppercase tracking-wider">— {hero.author}</div>
            </Link>

            <aside className="lg:col-span-4">
              <div className="border-l-2 border-[#DC2626] pl-3 md:pl-4 mb-4 md:mb-5">
                <div className="flex items-center gap-2 text-[#DC2626] uppercase text-[11px] md:text-xs tracking-widest font-admin font-semibold">
                  <TrendingUp className="w-3.5 h-3.5"/> ट्रेंडिंग अभी
                </div>
              </div>
              <ol className="space-y-3 md:space-y-5">
                {trending.map((n, i) => (
                  <li key={n.id}>
                    <Link to={`/article/${n.slug}`} className="flex gap-3 md:gap-4 group" data-testid={`trending-${i}`}>
                      <span className="font-devnagri-heading text-2xl md:text-4xl font-black text-slate-200 leading-none">{i+1}</span>
                      <div>
                        <div className="tag-cat text-[10px] mb-1">{n.category}</div>
                        <h3 className="font-devnagri-heading font-bold text-sm md:text-base leading-snug news-title text-[#0F172A]">{n.title}</h3>
                        <div className="text-[10px] md:text-[11px] text-slate-500 font-admin mt-1">{relTime(n.created_at)}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            </aside>
          </section>
        )}

        {featured.length > 0 && (
          <section className="mb-8 md:mb-12">
            <h2 className="font-devnagri-heading text-lg md:text-2xl font-bold mb-4 md:mb-6 pb-2 border-b-2 border-[#0F172A] inline-block">विशेष समाचार</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2 grid grid-cols-2 gap-3 md:gap-6">
              {featured.map((n) => (
                <Link key={n.id} to={`/article/${n.slug}`} className="group" data-testid={`featured-${n.id}`}>
                  <div className="img-hover-zoom relative rounded-md aspect-video bg-slate-200 mb-2 md:mb-3">
                    {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" loading="lazy"/>}
                    {n.video_url && (
                      <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-[#DC2626] text-white text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md shadow-lg">
                        <PlayCircle className="w-3 h-3"/> Video
                      </span>
                    )}
                  </div>
                  <div className="tag-cat text-[10px] mb-1 md:mb-1.5">{n.category}</div>
                  <h3 className="font-devnagri-heading font-bold text-sm md:text-lg leading-snug news-title text-[#0F172A] line-clamp-3">{n.title}</h3>
                  <div className="text-[10px] md:text-xs text-slate-500 font-admin mt-1 md:mt-2">{relTime(n.created_at)}</div>
                </Link>
              ))}
              </div>
              <TrendingSidebar />
            </div>
          </section>
        )}

        <AdSlot placement="home_middle" className="mb-8 md:mb-12"/>

        <EventsWidget />

        {editorials.length > 0 && (
          <section className="mb-8 md:mb-12 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white rounded-xl p-5 md:p-10 relative overflow-hidden" data-testid="editorial-section">
            <div className="absolute -top-6 -right-6 opacity-5 text-[160px] md:text-[220px] font-devnagri-heading font-black leading-none select-none">"</div>
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="w-1 h-6 md:h-8 bg-[#EA580C]"></div>
              <div>
                <div className="text-[#EA580C] uppercase text-[10px] md:text-xs tracking-widest font-admin font-bold">Editorial · संपादकीय</div>
                <div className="font-devnagri-heading text-lg md:text-3xl font-black">प्रधान संपादक की कलम से</div>
                <div className="text-slate-300 text-xs md:text-sm mt-0.5">JAKIR MOMIN · प्रधान संपादक</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {editorials.map((e, i) => (
                <Link key={e.id} to={`/article/${e.slug}`} className={`group ${i === 0 ? "md:col-span-2" : ""}`} data-testid={`editorial-${e.id}`}>
                  {(e.image_url || e.video_url) && i === 0 && (
                    <div className="img-hover-zoom relative rounded-lg aspect-video mb-3 md:mb-4 overflow-hidden">
                      {e.image_url && <img src={e.image_url} alt={e.title} className="w-full h-full object-cover opacity-90" loading="lazy"/>}
                      {e.video_url && (
                        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 bg-[#DC2626] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md shadow-lg">
                          <PlayCircle className="w-3.5 h-3.5"/> Video
                        </span>
                      )}
                    </div>
                  )}
                  <div className="text-[#EA580C] text-[10px] uppercase tracking-widest font-admin font-bold mb-1">{e.category}</div>
                  <h3 className={`font-devnagri-heading font-black leading-tight text-white group-hover:text-[#EA580C] transition ${i === 0 ? "text-lg md:text-3xl" : "text-base md:text-lg"}`}>{e.title}</h3>
                  {i === 0 && <p className="mt-2 md:mt-3 text-slate-300 text-sm md:text-base leading-relaxed line-clamp-3">{e.excerpt}</p>}
                  <div className="text-[10px] md:text-xs text-slate-400 mt-2 md:mt-3">{relTime(e.created_at)}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {rest.length > 0 && (
          <section>
            <h2 className="font-devnagri-heading text-lg md:text-2xl font-bold mb-4 md:mb-6 pb-2 border-b-2 border-[#0F172A] inline-block">और समाचार</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
              {rest.map((n) => (
                <Link key={n.id} to={`/article/${n.slug}`} className="group flex gap-3 md:gap-5" data-testid={`more-${n.id}`}>
                  <div className="img-hover-zoom relative rounded-md w-24 md:w-40 aspect-video bg-slate-200 shrink-0">
                    {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" loading="lazy"/>}
                    {n.video_url && (
                      <span className="absolute top-1 left-1 md:top-1.5 md:left-1.5 z-10 inline-flex items-center gap-1 bg-[#DC2626] text-white text-[8px] md:text-[9px] font-black uppercase tracking-widest px-1 md:px-1.5 py-0.5 rounded-md shadow-lg">
                        <PlayCircle className="w-2.5 h-2.5 md:w-3 md:h-3"/> Video
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="tag-cat text-[10px] mb-1">{n.category}</div>
                    <h3 className="font-devnagri-heading font-bold text-sm md:text-lg leading-snug news-title text-[#0F172A] line-clamp-3">{n.title}</h3>
                    <div className="text-[10px] md:text-xs text-slate-500 font-admin mt-1 md:mt-2">{relTime(n.created_at)} · {n.author}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <PublicFooter />
      <InstallAppButton />
    </>
  );
}
